import { FeedbackType } from '@enview/interface/types/actions/FeedbackData';
import { ReactElement, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as BillAnalytics from '../../../analytics/BillAnalytics';
import { BillAPI } from '../../../api';
import FeedbackPrompt from '../Prompts/FeedbackPrompt';
import { formatPlainTextToReact } from './utils';

type BillVersionToVersionAutoSummaryProps = {
  billId: string;
  billVersionAId: number;
  billVersionBId: number;
};

/**
 * Demo component to fetch and display a generated bill summary
 * from the Data Insights API
 *
 * @param props
 * @constructor
 */
const BillVersionToVersionAutoSummary = (
  props: BillVersionToVersionAutoSummaryProps,
): ReactElement => {
  const { billId, billVersionAId, billVersionBId } = props;
  const {
    data: summaryResponse,
    isError,
    isLoading,
  } = BillAPI.endpoints.getBillVersionToVersionAutoSummary.useQuery({
    billId,
    billVersionAId,
    billVersionBId,
  });
  const [isVersionToVersionSummaryAccurate, setIsVersionToVersionSummaryAccurate] =
    useState<boolean | undefined>(undefined);
  const { t } = useTranslation();

  useEffect(() => {
    if (isVersionToVersionSummaryAccurate !== undefined && summaryResponse?.summary) {
      BillAnalytics.trackBillVersionToVersionSummaryFeedback(
        billId,
        billVersionAId,
        billVersionBId,
        isVersionToVersionSummaryAccurate,
        summaryResponse.summary,
      );
    }
  }, [isVersionToVersionSummaryAccurate, summaryResponse?.summary]);

  const getText = (): ReactElement[] | ReactElement | string => {
    if (summaryResponse && summaryResponse.insightsApiError) {
      return t(summaryResponse.errorResourceCode);
    }
    if (summaryResponse && summaryResponse.summary) {
      return (
        <>
          <p className="ai-disclaimer-message">{t('bill.sidebar.aiDisclaimer')}</p>
          {formatPlainTextToReact(summaryResponse.summary)}
          <FeedbackPrompt
            feedbackType={FeedbackType.VersionToVersionSummaryInaccurate}
            setFeedbackState={setIsVersionToVersionSummaryAccurate}
            submitFeedbackMetadata={{
              billId: billId,
              billVersionId1: billVersionAId,
              billVersionId2: billVersionBId,
              summary: summaryResponse.summary,
            }}
          />
        </>
      );
    }
    if (isError) {
      return t('error.genericApiErrorMessage');
    }
    return <></>;
  };

  if (isLoading) {
    return (
      <div className="my-5" style={{ width: '25px' }}>
        <Spinner animation="border" role="status" variant="secondary" />
      </div>
    );
  }

  return <div className="ai-generated-text">{getText()}</div>;
};

export default BillVersionToVersionAutoSummary;
